import React, { useEffect } from "react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { useAppDispatch, useAppSelector } from "../../store";
import { useParams } from "react-router-dom";
import {
  AdminContentWrapper,
  AdminDetailInfoItem,
  AdminDetailPageHeader,
  AdminDetailsPageItemName,
  Button,
  Loader,
} from "../../components";
import { setDataLoading } from "../../store/slices/admin";
import {
  clearAdminEigentuemerwechselItem,
  getAdminEigentuemerwechselItemById,
} from "../../store/slices/eigentuemerwechsel";
import appConfig from "../../util/appConfig";
import API from "../../api/api";
import Lra from "./lra";
import Mags from "./mags";
import Viersen from "./viersen";

export const statuses: { [key: string]: string } = {
  accepted: "Fertig",
  declined: "Storniert",
  inWork: "Zu bearbeiten",
  check: "Prüfen",
};

const tonnenType = {
  available: "Sind vorhanden",
  additional: "Folgenden Größen liefern:",
  remove: "Keine Bio/Papiertonne erforderlich",
};

const AdminEigentuemerwechselById = () => {
  const dispatch = useAppDispatch();
  const { itemId } = useParams();

  const { isAdminLoaderActive } = useAppSelector((state) => state.admin);
  const { isDataLoading, eigentuemerwechselItem } = useAppSelector(
    (state) => state.eigentuemerwechsel.admin,
  );

  useEffect(() => {
    if (itemId) {
      dispatch(getAdminEigentuemerwechselItemById(itemId));
    }
    return () => {
      dispatch(clearAdminEigentuemerwechselItem());
      dispatch(setDataLoading(false));
    };
  }, []);

  if (!eigentuemerwechselItem) {
    return null;
  }

  const items = [
    {
      title: "Angaben zum Objekt",
      items: [
        {
          rowTitle: "Objektnummer",
          rowInfo: eigentuemerwechselItem?.objectId || "",
        },
        ...(["viersen"].includes(appConfig.logo)
          ? [
              {
                rowTitle: "Kassenzeichen",
                rowInfo: eigentuemerwechselItem?.kassenzeichen || "",
              },
            ]
          : []),
        {
          rowTitle: "Straße",
          rowInfo: eigentuemerwechselItem?.strasse || "",
        },
        {
          rowTitle: "Hausnummer",
          rowInfo: `${eigentuemerwechselItem?.hausnr}${
            eigentuemerwechselItem?.hausnrzusatz
              ? eigentuemerwechselItem.hausnrzusatz
              : ""
          }${eigentuemerwechselItem?.hausnrto ? ` - ${eigentuemerwechselItem?.hausnrto}` : ""}`,
        },
        // {
        //   rowTitle: "PLZ",
        //   rowInfo: "",
        // },
        {
          rowTitle: "Ort",
          rowInfo: eigentuemerwechselItem?.ort || "",
        },
        ...(["mags", "viersen"].includes(appConfig.logo) &&
        eigentuemerwechselItem?.pdfName?.length
          ? [
              {
                rowTitle: "Dokument",
                rowInfo: "",
                rowCustomItem: (
                  <div
                    style={{
                      display: "flex",
                      gap: "20px",
                      flexWrap: "wrap",
                    }}
                  >
                    {eigentuemerwechselItem?.pdfName.map((doc) => {
                      return (
                        <Button
                          text={"Dokument herunterladen" + ` ${doc}`}
                          width="200"
                          onClick={async () => {
                            if (itemId && eigentuemerwechselItem?.pdfName) {
                              await API.eigentuemerwechsel.get.getAdminItemPdfByPdfName(
                                itemId,
                                doc,
                              );
                            }
                          }}
                          type="button"
                          outlineColor={appConfig.mainColor}
                          textColor={appConfig.mainColor}
                          isOutlined
                          key={doc}
                          btnStyle={{
                            width: "auto",
                            padding: "10px",
                          }}
                        />
                      );
                    })}
                  </div>
                ),
              },
            ]
          : [{}]),
        ...(["mags", "viersen"].includes(appConfig.logo)
          ? [
              {
                rowTitle: "Status",
                rowInfo: eigentuemerwechselItem?.status
                  ? statuses[eigentuemerwechselItem?.status]
                  : "-",
              },
            ]
          : [
              {
                rowTitle: "Bio- und Papiertonnen",
                rowInfo: eigentuemerwechselItem?.status
                  ? statuses[eigentuemerwechselItem?.status]
                  : "-",
              },
            ]),
        ...(["mags"].includes(appConfig.logo)
          ? [
              {
                rowTitle: "Bio- und Papiertonnen",
                rowInfo: eigentuemerwechselItem?.tonnenType
                  ? tonnenType[eigentuemerwechselItem?.tonnenType]
                  : "-",
                rowCustomItem:
                  eigentuemerwechselItem?.tonnenType === "additional" ? (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        flexDirection: "column",
                      }}
                    >
                      {eigentuemerwechselItem?.additionalBioBin ? (
                        <div>
                          - Biotonne: {eigentuemerwechselItem.additionalBioBin}
                          L{" "}
                        </div>
                      ) : null}
                      {eigentuemerwechselItem?.additionalPaperBin ? (
                        <div>
                          - Papiertonne:{" "}
                          {eigentuemerwechselItem.additionalPaperBin}L{" "}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <></>
                  ),
              },
            ]
          : []),
      ],
    },
    {
      title: ["mags"].includes(appConfig.logo)
        ? "Bisheriger Eigentümer"
        : "Bisheriger Eigentümer Detailinfo",
      items: [
        {
          rowTitle: "Name",
          rowInfo: `${eigentuemerwechselItem?.previousOwnerName}`,
        },
        {
          rowTitle: "Vorname",
          rowInfo: `${eigentuemerwechselItem?.previousOwnerSurname}`,
        },
        {
          rowTitle: "Straße",
          rowInfo: `${eigentuemerwechselItem?.previousOwnerStrasse}`,
        },
        {
          rowTitle: "Hausnummer",
          rowInfo: `${eigentuemerwechselItem?.previousOwnerHausnr}${
            eigentuemerwechselItem?.previousOwnerHausnrzusatz
              ? eigentuemerwechselItem.previousOwnerHausnrzusatz
              : ""
          }${eigentuemerwechselItem?.previousOwnerHausnrTo ? ` - ${eigentuemerwechselItem?.previousOwnerHausnrTo}` : ""}`,
        },
        {
          rowTitle: "PLZ",
          rowInfo: `${eigentuemerwechselItem?.previousOwnerPlz}`,
        },
        {
          rowTitle: "Ort",
          rowInfo: `${eigentuemerwechselItem?.previousOwnerOrt}`,
        },
        {
          rowTitle: "Telefonnummer",
          rowInfo: `${eigentuemerwechselItem?.previousOwnerPhoneNumber}`,
        },
        {
          rowTitle: "E-Mail Adresse",
          rowInfo: `${eigentuemerwechselItem?.previousOwnerEmail}`,
        },
      ],
    },
    {
      title: ["mags"].includes(appConfig.logo)
        ? "Neuer Eigentümer"
        : "Neuer Eigentümer Detailinfo",
      items: [
        {
          rowTitle: "Name",
          rowInfo: `${eigentuemerwechselItem?.newOwnerName}`,
        },
        {
          rowTitle: "Vorname",
          rowInfo: `${eigentuemerwechselItem?.newOwnerSurname}`,
        },
        {
          rowTitle: "Straße",
          rowInfo: `${eigentuemerwechselItem?.previousOwnerStrasse}`,
        },
        {
          rowTitle: "Hausnummer",
          rowInfo: `${eigentuemerwechselItem?.newOwnerHausnr}${
            eigentuemerwechselItem?.newOwnerHausnrzusatz
              ? eigentuemerwechselItem.newOwnerHausnrzusatz
              : ""
          }${eigentuemerwechselItem?.newOwnerHausnrTo ? ` - ${eigentuemerwechselItem?.newOwnerHausnrTo}` : ""}`,
        },
        {
          rowTitle: "PLZ",
          rowInfo: `${eigentuemerwechselItem?.newOwnerPlz}`,
        },
        {
          rowTitle: "Ort",
          rowInfo: `${eigentuemerwechselItem?.newOwnerOrt}`,
        },
        {
          rowTitle: "Telefonnummer",
          rowInfo: `${eigentuemerwechselItem?.newOwnerPhoneNumber}`,
        },
        {
          rowTitle: "E-Mail Adresse",
          rowInfo: `${eigentuemerwechselItem?.newOwnerEmail}`,
        },
        {
          rowTitle: "Personenanzahl",
          rowInfo: `${eigentuemerwechselItem?.newOwnerQuantityOfPeople}`,
        },
        ...(eigentuemerwechselItem?.newOwnerSince
          ? [
              {
                rowTitle: "Eigentümer seit",
                rowInfo: format(
                  new Date(eigentuemerwechselItem?.newOwnerSince),
                  "dd.MM.yyyy",
                  {
                    locale: de,
                  },
                ),
              },
            ]
          : []),
        ...(eigentuemerwechselItem?.quantityOfObjects
          ? [
              {
                rowTitle: "Menge der Haushalte",
                rowInfo: eigentuemerwechselItem?.quantityOfObjects,
              },
            ]
          : []),
        ...(eigentuemerwechselItem?.transferDate
          ? [
              {
                rowTitle: "Mülltonnenübertragung zum",
                rowInfo: format(
                  new Date(eigentuemerwechselItem?.transferDate),
                  "dd.MM.yyyy",
                  {
                    locale: de,
                  },
                ),
              },
            ]
          : []),
      ],
    },
  ];

  return (
    <div
      style={{
        position: "relative",
      }}
    >
      <AdminDetailPageHeader isBtnAvailable={false} />
      <AdminContentWrapper>
        <AdminDetailsPageItemName text="Detailinformationen" />
        {isDataLoading ? null : !eigentuemerwechselItem ? null : <div></div>}
        <div>
          {items.map((el, idx) => {
            return (
              <AdminDetailInfoItem
                items={el.items}
                title={el.title}
                key={idx}
              />
            );
          })}
        </div>
        {["lra"].includes(appConfig.logo) ? <Lra /> : null}
        {["mags"].includes(appConfig.logo) ? (
          <Mags itemId={itemId ?? ""} />
        ) : null}
        {["viersen"].includes(appConfig.logo) ? (
          <Viersen itemId={itemId ?? ""} />
        ) : null}
      </AdminContentWrapper>
      {isAdminLoaderActive && <Loader />}
    </div>
  );
};

export default AdminEigentuemerwechselById;
